import React from "react";
import i18next from "src/i18n/config";
import { makeStyles } from "tss-react/mui";

import {
    Box,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogProps,
    DialogTitle,
    Theme,
    Typography,
} from "@mui/material";

import { SecondaryButton } from "../CustomButton";

const useStyles = makeStyles()((theme: Theme) => ({
    description: { marginBottom: theme.spacing(2) },
    actionBox: { width: "100%", display: "flex", justifyContent: "space-between", alignItems: "center" },
    startButtonBox: { display: "flex", gap: theme.spacing(1), flex: 1 },
    endButtonBox: { display: "flex", gap: theme.spacing(1) },
    childrenBox: { display: "flex", flexDirection: "column", gap: theme.spacing(2) },
}));

type Props = Omit<DialogProps, "onClose" | "maxWidth" | "fullWidth"> & {
    /**
     * `"sm"` (600px, the default) for confirmations, filters and forms made of fields. `"md"` (900px) for code or
     * JSON, tables with several columns, long identifiers such as hashes, and tabs where one tab holds an editor.
     * The dialog always fills its size, so the content never decides the width.
     */
    maxWidth?: "sm" | "md";
    titleSuffix?: React.ReactNode;
    description?: string | React.JSX.Element;
    /**
     * Additional actions that are not the dialog's purpose (e.g. "Get help", "Expand all"), placed at the left edge.
     */
    startButton?: React.ReactNode;
    /**
     * The dialog's primary action, placed at the right edge after the close button.
     */
    endButton?: React.ReactNode;
    secondaryActionText?: string;
    onClose?: (event?: object, reason?: "backdropClick" | "escapeKeyDown") => void;
};

export const Modal = ({
    title,
    titleSuffix = null,
    description,
    startButton,
    endButton,
    secondaryActionText = i18next.t("common:button.close"),
    ...props
}: React.PropsWithChildren<Props>) => {
    const { classes } = useStyles();

    const handleClose = (event?: object, reason?: "backdropClick" | "escapeKeyDown") => {
        if (document.activeElement instanceof HTMLElement) {
            document.activeElement.blur();
        }
        props.onClose?.(event, reason);
    };

    // Escape is handled by MUI alone, which calls `onClose` with "escapeKeyDown" and honours `disableEscapeKeyDown`.
    // Do not add a second Escape handler here or in a dialog: `onClose` would run twice, and a dialog whose
    // `onClose` toggles its open state reopens straight away.
    return (
        <Dialog fullWidth {...props} onClose={handleClose}>
            <DialogTitle>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="h6">{title}</Typography>
                    <Box>{titleSuffix}</Box>
                </Box>
            </DialogTitle>
            <DialogContent dividers>
                {description ? <DialogContentText className={classes.description}>{description}</DialogContentText> : null}
                <Box className={classes.childrenBox}>{props.children}</Box>
            </DialogContent>

            <DialogActions>
                <Box className={classes.actionBox}>
                    <Box className={classes.startButtonBox}>{startButton}</Box>

                    <Box className={classes.endButtonBox}>
                        {props.onClose ? <SecondaryButton onClick={handleClose}>{secondaryActionText}</SecondaryButton> : null}
                        {endButton}
                    </Box>
                </Box>
            </DialogActions>
        </Dialog>
    );
};
